<script setup lang="ts">
import type { ShoppingList } from '~/types'

useSeoMeta({
    title: 'Cuba Dulu — Groceries',
})

const { loggedIn, user } = useAuth()
const firstName = computed(() => user.value?.name?.split(' ')[0] || 'Anda')

const sampleList: ShoppingList = {
    uuid: 'demo',
    name: 'Barang Dapur Mingguan',
    is_completed: false,
    completed_at: null,
    expires_at: null,
    is_expired: false,
    owner_name: 'Contoh',
    items_count: 6,
    completed_items_count: 1,
    items: [
        {
            id: 1,
            name: 'Beras 5kg',
            quantity: 1,
            unit: 'bungkus',
            display_quantity: '1 bungkus',
            category: 'Barang Kering',
            notes: null,
            is_completed: false,
            completed_at: null,
            sort_order: 0,
        },
        {
            id: 2,
            name: 'Telur Gred A',
            quantity: 30,
            unit: 'biji',
            display_quantity: '30 biji',
            category: 'Tenusu & Telur',
            notes: null,
            is_completed: false,
            completed_at: null,
            sort_order: 1,
        },
        {
            id: 3,
            name: 'Minyak Masak',
            quantity: 2,
            unit: 'botol',
            display_quantity: '2 botol',
            category: 'Barang Kering',
            notes: 'jenama biasa',
            is_completed: false,
            completed_at: null,
            sort_order: 2,
        },
        {
            id: 4,
            name: 'Susu Cair',
            quantity: 2,
            unit: 'kotak',
            display_quantity: '2 kotak',
            category: 'Tenusu & Telur',
            notes: null,
            is_completed: false,
            completed_at: null,
            sort_order: 3,
        },
        {
            id: 5,
            name: 'Cili Padi',
            quantity: 1,
            unit: 'pek',
            display_quantity: '1 pek',
            category: 'Sayur-sayuran',
            notes: null,
            is_completed: false,
            completed_at: null,
            sort_order: 4,
        },
        {
            id: 6,
            name: 'Milo',
            quantity: 1,
            unit: 'tin',
            display_quantity: '1 tin',
            category: 'Minuman',
            notes: null,
            is_completed: true,
            completed_at: new Date().toISOString(),
            sort_order: 5,
        },
    ],
}
</script>

<template>
    <div class="space-y-4 py-2">
        <!-- Hero banner -->
        <div
            class="card text-center"
            :style="{
                background: 'linear-gradient(135deg, var(--green-soft), var(--panel-soft))',
                borderColor: 'var(--green)',
            }"
        >
            <p class="kicker">👋 Cuba Dulu</p>
            <p class="mt-1.5 text-sm" :style="{ color: 'var(--muted)' }">
                Ini contoh senarai — {{ firstName }} boleh intai dulu macam mana rupa app ni.
                <NuxtLink v-if="!loggedIn" to="/auth/register" class="font-semibold underline"
                    >Daftar percuma</NuxtLink
                >
                <span v-else>Selamat datang balik!</span>
            </p>
        </div>

        <!-- Items preview -->
        <section aria-label="Item dalam senarai">
            <ul class="space-y-2">
                <li
                    v-for="item in sampleList.items"
                    :key="item.id"
                    class="card flex items-center gap-3 px-4 py-3 transition-opacity"
                    :class="{ 'opacity-50': item.is_completed }"
                >
                    <span
                        class="size-5 shrink-0 rounded-full border-2"
                        :class="
                            item.is_completed ? 'border-green-500 bg-green-500' : 'border-gray-400'
                        "
                        :style="{
                            background: item.is_completed ? 'var(--green)' : 'transparent',
                            borderColor: item.is_completed ? 'var(--green)' : 'var(--line)',
                        }"
                    >
                        <span
                            v-if="item.is_completed"
                            class="flex size-full items-center justify-center text-[10px] text-white"
                            >✓</span
                        >
                    </span>

                    <div class="min-w-0 flex-1">
                        <p
                            class="truncate text-sm font-medium"
                            :class="{ 'line-through': item.is_completed }"
                        >
                            {{ item.name }}
                        </p>
                        <p
                            v-if="item.display_quantity"
                            class="text-xs"
                            :style="{ color: 'var(--muted)' }"
                        >
                            {{ item.display_quantity }}
                            <span v-if="item.category" class="ml-1.5 opacity-60"
                                >· {{ item.category }}</span
                            >
                        </p>
                    </div>

                    <span
                        v-if="item.category"
                        class="hidden shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium sm:inline-block"
                        :style="{ background: 'var(--surface-2)', color: 'var(--muted)' }"
                    >
                        {{ item.category }}
                    </span>
                </li>
            </ul>
        </section>

        <!-- CTA -->
        <div class="card py-6 text-center">
            <p class="text-sm" :style="{ color: 'var(--muted)' }">
                Macam mana? Dah nampak potensi? Jom buat senarai {{ firstName }} sendiri.
            </p>
            <div class="mt-4 flex flex-col justify-center gap-3 sm:flex-row">
                <NuxtLink
                    v-if="!loggedIn"
                    to="/auth/register"
                    class="btn-primary !min-h-[44px] !px-6 text-sm"
                >
                    Daftar percuma — 2 minit je
                </NuxtLink>
                <NuxtLink
                    v-if="loggedIn"
                    to="/lists/create"
                    class="btn-primary !min-h-[44px] !px-6 text-sm"
                >
                    Buat senarai baru
                </NuxtLink>
                <NuxtLink v-if="!loggedIn" to="/auth/login" class="btn !min-h-[44px] !px-6 text-sm">
                    Saya dah ada akaun
                </NuxtLink>
            </div>
        </div>
    </div>
</template>
